/**
 * JobsDrawer - the global Jobs drawer (Story #5351 W1-T10, Screen Contract
 * G04). Canonical route per the workbook: `/operations/jobs?drawer=1&jobId=
 * {jobId}` — this component is URL-driven off that same query-string shape
 * regardless of which page it is mounted on (the drawer is chrome, not a
 * page), so opening a job, reloading, handing the link to a different
 * operator, or the browser restarting all land on the exact same job.
 *
 * Reads the real ingestion-jobs API (jobsApi.getJob, existing in
 * services/api.ts — no new endpoint). Rendering the read result honestly is
 * DurableJobStatus's job; this component owns only the drawer shell and the
 * fetch.
 *
 * Deliberately NOT mounted into Header/Layout by this task — those files
 * are components/layout/*, outside this task's file ownership (same
 * handoff ScopeRibbon.tsx and HelpDrawer.tsx recorded). See the PR body.
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { jobsApi } from '../../services/api';
import Drawer from '../ui/Drawer';
import StatePanel from '../ui/StatePanel';
import DurableJobStatus from './DurableJobStatus';
import { isW1Shell } from '../../utils/w1Flag';

const DRAWER_PARAM = 'drawer';
const JOB_ID_PARAM = 'jobId';

const JobsDrawer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Render-time flag read (never module scope — see utils/w1Flag.ts). Hooks
  // below must still run unconditionally, so the flag check alone does not
  // gate hook calls — only what gets returned.
  const flagOn = isW1Shell();
  const isOpen = searchParams.get(DRAWER_PARAM) === '1';
  const jobId = searchParams.get(JOB_ID_PARAM);

  const { data: job, isLoading, isError } = useQuery({
    queryKey: ['w1-jobs-drawer', jobId],
    queryFn: () => jobsApi.getJob(jobId as string),
    enabled: flagOn && isOpen && !!jobId,
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === 'RUNNING' ? 5000 : false),
  });

  if (!flagOn || !isOpen) return null;

  const onClose = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(DRAWER_PARAM);
    next.delete(JOB_ID_PARAM);
    setSearchParams(next);
  };

  return (
    <Drawer onClose={onClose} ariaLabel="Job status" maxWidthClassName="max-w-md" panelClassName="bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <h2 className="text-base font-semibold text-gray-900">Jobs</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close jobs"
          className="p-1 rounded hover:bg-gray-100 text-gray-500"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {jobId ? (
          <DurableJobStatus jobId={jobId} job={job} isLoading={isLoading} isError={isError} />
        ) : (
          <StatePanel state="empty" message="No job selected." />
        )}
      </div>
    </Drawer>
  );
};

export default JobsDrawer;
